import React, { useMemo, useState } from 'react'
import DetailsArea from './detailsArea'
import { SchemaContext, EditModeContext, components, defaults, ErrorPathContext } from './detailsPageComponents'

export default function DetailsPage({
  title,
  id, fields, schema, onSubmit, editOnly = false, rootLevel = '', onEditToggled, saveWithoutChanges,
  readOnly = false,
  buttonProps = {/* edit, cancel, submit, other */},
  extraButtons = [/* {title, onClick, enabledOnEdit, enabledOnView} */],
  ...props
}) {
  const { Button } = components
  const [editMode, setEditMode] = useState(editOnly)

  function handleEdit(edit) {
    setEditMode(edit)
    if (onEditToggled && edit !== editMode) onEditToggled(edit)
  }

  const [leftHeaderButtons, rightHeaderButtons] = useMemo(() => {
    const cancelBtnProps = { ...defaults.headerButton?.all, ...defaults.headerButton?.cancel, ...buttonProps?.cancel }
    const editBtnProps = { ...defaults.headerButton?.all, ...defaults.headerButton?.edit, ...buttonProps?.edit }
    const submitBtnProps = { ...defaults.headerButton?.all, ...defaults.headerButton?.submit, ...buttonProps?.submit }
    const otherBtnProps = { ...defaults.headerButton?.all, ...defaults.headerButton?.other, ...buttonProps?.other }
    const btns = extraButtons.map(({ disabled, enabledOnEdit, enabledOnView, ...buttonProps }) => {
      return { ...otherBtnProps, ...buttonProps, disabled: disabled || (editMode ? !(enabledOnEdit ?? true) : !(enabledOnView ?? true)) }
    })

    if (editMode) {
      btns.push({
        title: 'Cancel',
        onClick: () => {
          schema.reset()
          handleEdit(false)
        },
        ...cancelBtnProps
      },
      { title: 'Save', disabled: !saveWithoutChanges && !schema.isDirty(), ...submitBtnProps, onClick: () => onSubmit().then(() => handleEdit(false)).catch(console.error) })
    } else if (!readOnly) {
      btns.unshift({ title: 'Edit', ...editBtnProps, onClick: () => handleEdit(true) })
    }
    return btns.reduce(([l, r], btn) => {
      if (btn.position === 'left') return [[...l, btn], r]
      else if (!btn.position || btn.position === 'right') return [l, [...r, btn]]
      else return [l, r]
    }, [[], []])
  }, [buttonProps, extraButtons, defaults.headerButton, editMode])

  if (!fields?.length) return null

  return (
    <div className={'schema-form' + (props.className ? ` ${props.className}` : '')}>
      <div className="schema-form__container">
        <div className={'schema-form__header' + (props.headerClassName ? ` ${props.headerClassName}` : '')}>
          <div className="schema-form__header-title">{title}</div>
          <div className="schema-form__header-id">{id}</div>
          {
            leftHeaderButtons?.length
              ? (
                <div className='schema-form__header-left-buttons'>
                  {leftHeaderButtons.map(({ title, onClick, ...buttonProps }, i) => {
                    return (
                      <>
                        {
                          buttonProps?.separator === 'left' && <div className="schema-form__header-button-separator"/>
                        }
                        <Button key={i} iconTitle={title} {...(buttonProps || {})} onClick={onClick}>{title}</Button>
                        {
                          buttonProps?.separator === 'right' && <div className="schema-form__header-button-separator"/>
                        }
                      </>
                    )
                  }
                  )}
                </div>
              )
              : null
          }
          <div className='schema-form__header-right-buttons'>
            {rightHeaderButtons.map(({ title, onClick, ...buttonProps }, i) => {
              return (
                <>
                  {
                    buttonProps?.separator === 'left' && <div className="schema-form__header-button-separator"/>
                  }
                  <Button key={i} iconTitle={title} {...(buttonProps || {})} onClick={onClick}>{title}</Button>
                  {
                    buttonProps?.separator === 'right' && <div className="schema-form__header-button-separator"/>
                  }
                </>
              )
            }
            )}
          </div>
        </div>
        <SchemaContext.Provider value={schema}>
          <EditModeContext.Provider value={editMode}>
            <ErrorPathContext.Provider value={rootLevel}>
              <DetailsArea fields={fields} />
            </ErrorPathContext.Provider>
          </EditModeContext.Provider>
        </SchemaContext.Provider>
      </div>
    </div>
  )
}
